if (!customElements.get('fabric-buying')) {
  customElements.define('fabric-buying', class extends HTMLElement {
    connectedCallback() {
      Promise.all(['variant-radios', 'product-info', 'product-form'].map(name => customElements.whenDefined(name))).then(() => this.init());
    }
    init() {
      if (this.ready || !this.isConnected || Number(this.dataset.optionCount) !== 1) return;
      this.root = this.closest('product-info');
      this.native = this.root?.querySelector('variant-radios');
      this.sourceSection = this.native?.dataset.originalSection || this.root?.dataset.originalSection || this.dataset.section;
      this.isQuickAdd = this.sourceSection !== this.dataset.section;
      this.quantity = this.root?.querySelector('.quantity__input');
      this.form = this.root?.querySelector('product-form form');
      this.submit = this.form?.querySelector('[name="add"]');
      try {
        this.variants = JSON.parse(this.querySelector('[data-fabric-variants]').textContent);
        this.labels = JSON.parse(this.querySelector('[data-fabric-labels]').textContent);
      } catch { return; }
      if (!this.supportsVariants(this, this.variants)) return;
      this.defaultMetre = this.dataset.metreDefault === 'true' && this.variants.length === 1 && this.variants[0].title === 'Default Title';
      const metres = this.variants.filter(v => /^per met(?:er|re)?$/i.test(v.title.trim()));
      this.metre = this.defaultMetre ? this.variants[0] : metres.length === 1 ? metres[0] : null;
      this.length = v => Number(v.title.trim().match(/^roll of\s+(\d+(?:\.\d+)?)\s*met$/i)?.[1] || 0);
      this.rolls = this.variants.filter(v => this.length(v) > 0);
      if (!this.quantity || !this.form || !this.submit || !this.metre || this.rolls.length !== this.variants.length - 1 || !this.root.querySelector('.product-form__quantity') || !this.form.querySelector('.product-form__buttons')) return;
      if (!this.defaultMetre && (!this.native || this.native.querySelectorAll('fieldset').length !== 1 || !this.variants.every(v => [...this.native.querySelectorAll('input[type="radio"]')].some(input => input.value === v.title)))) return;
      this.ready = true;
      if (this.isQuickAdd) {
        this.root.variantChangeUnsubscriber?.();
        const oldFormId = this.form.getAttribute('id');
        const instance = this.constructor.instanceCount = (this.constructor.instanceCount || 0) + 1;
        const newFormId = `${oldFormId}-fabric-${instance}`;
        this.form.setAttribute('id', newFormId);
        for (const control of this.root.querySelectorAll('[form]')) {
          if (control.getAttribute('form') === oldFormId) control.setAttribute('form', newFormId);
        }
        for (const radio of this.root.querySelectorAll('input[type="radio"][name]')) {
          radio.name = `${radio.name}-fabric-${instance}`;
        }
      }
      this.money = cents => new Intl.NumberFormat(this.dataset.locale || 'en-ZA', {style:'currency',currency:this.dataset.currency || 'ZAR',currencyDisplay:'narrowSymbol',minimumFractionDigits:2}).format(cents / 100);
      this.formatInputs = [...this.querySelectorAll('input[type="radio"]')];
      this.rollSelect = this.querySelector('select');
      this.rollField = this.querySelector('.fabric-roll-field');
      this.status = this.querySelector('.fabric-status');
      this.unitPrice = this.querySelector('.fabric-unit-price');
      this.quantities = {metre:Math.max(this.metre.min || 1, Number(this.quantity.value) || 1),roll:1};
      this.querySelector('.fabric-formats').hidden = this.rolls.length === 0;
      this.refreshOptions();
      this.detail = document.createElement('p');
      this.detail.className = 'fabric-quantity-detail';
      this.detail.setAttribute('aria-live','polite');
      this.root.querySelector('.product-form__quantity').append(this.detail);
      this.stock = document.createElement('p');
      this.stock.className = 'fabric-stock';
      this.stock.setAttribute('aria-live', 'polite');
      this.root.querySelector('.product-form__quantity').append(this.stock);
      const guideSection = [...document.querySelectorAll('.shopify-section')].find(section => /How much fabric do I need\?/i.test(section.querySelector('h1,h2,h3')?.textContent || ''));
      if (guideSection) {
        const guide = document.createElement('div');
        guide.className = 'fabric-guide';
        const link = document.createElement('a');
        link.href = '#' + guideSection.id;
        link.textContent = this.labels.guide;
        const small = document.createElement('small');
        small.textContent = this.labels.guideDetail;
        guide.append(link,small);
        this.root.querySelector('.product-form__quantity').after(guide);
      }
      this.total = document.createElement('div');
      this.total.className = 'fabric-total';
      this.total.setAttribute('role','status');
      this.total.setAttribute('aria-live','polite');
      this.form.querySelector('.product-form__buttons').prepend(this.total);
      this.form.closest('product-form').classList.add('fabric-native-form');
      this.retry = document.createElement('button');
      this.retry.type = 'button';
      this.retry.className = 'button button--secondary fabric-retry';
      this.retry.textContent = this.labels.retry;
      this.retry.hidden = true;
      this.retry.addEventListener('click', () => this.select(this.current.id));
      this.status.after(this.retry);
      if (this.native) {
        if (this.isQuickAdd) {
          this.originalUpdateVariantInput = this.native.updateVariantInput;
          this.native.updateVariantInput = () => {
            if (!this.native.currentVariant) return;
            for (const input of this.root.querySelectorAll('form [name="id"]')) {
              input.value = this.native.currentVariant.id;
              input.dispatchEvent(new Event('change', {bubbles:true}));
            }
          };
          this.originalToggleAddButton = this.native.toggleAddButton;
          this.native.toggleAddButton = (disable = true, text) => {
            this.submit.disabled = disable;
            const label = this.submit.querySelector('span');
            if (label && (!disable || text)) label.textContent = disable ? text : window.variantStrings.addToCart;
          };
        }
        this.originalRenderProductInfo = this.native.renderProductInfo;
        this.native.renderProductInfo = () => this.loadSelection();
      }
      this.root.cartUpdateUnsubscriber?.();
      this.root.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.desiredQuantity = Number(this.quantity.value) || 1;
        this.loadSelection();
      });
      if (this.native) {
        this.originalUpdateUrl = this.native.updateURL;
        this.native.updateURL = () => {
          if (!this.native.currentVariant || this.native.dataset.updateUrl === 'false') return;
          const url = new URL(window.location.href);
          url.searchParams.set('variant',this.native.currentVariant.id);
          window.history.replaceState(window.history.state,'',url);
        };
      }
      this.addEventListener('change',event => {
        event.stopPropagation();
        if (event.target === this.rollSelect) this.select(Number(this.rollSelect.value));
        else if (this.formatInputs.includes(event.target)) {
          const v = event.target.value === 'metre' ? this.metre : this.rolls.find(v => v.id === this.lastRoll && v.available) || this.rolls.find(v => v.available) || this.rolls[0];
          if (v) this.select(v.id);
        }
      });
      this.quantity.addEventListener('input',() => this.quantityChanged());
      this.quantity.addEventListener('change',() => this.quantityChanged());
      this.form.addEventListener('submit',event => {
        const id = Number(this.form.querySelector('[name="id"]').value);
        if (this.pending || this.failed || id !== this.current.id || !this.current.available || !this.validQuantity()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.status.textContent = this.pending ? this.labels.updating : this.current.available ? this.labels.quantityError : this.labels.soldOut;
          this.quantity.reportValidity();
        }
      },true);
      this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange,event => {
        if (event.data.source && event.data.source !== this) return;
        if (event.data.sectionId !== this.dataset.section || Number(event.data.variant.id) !== this.current?.id) return;
        if (this.isQuickAdd) {
          this.root.updateQuantityRules(this.sourceSection, event.data.html);
          const updatedQuantity = event.data.html.getElementById(`Quantity-Form-${this.sourceSection}`)?.querySelector('.quantity__input');
          if (updatedQuantity) {
            for (const attribute of ['data-cart-quantity', 'data-min', 'data-max', 'step']) {
              const value = updatedQuantity.getAttribute(attribute);
              if (value === null) this.quantity.removeAttribute(attribute);
              else this.quantity.setAttribute(attribute, value);
            }
          }
          this.root.setQuantityBoundries();
        }
        const freshComponent = event.data.html.querySelector('fabric-buying');
        let freshVariants;
        try { freshVariants = JSON.parse(freshComponent?.querySelector('[data-fabric-variants]')?.textContent); } catch { freshVariants = null; }
        if (!this.supportsVariants(freshComponent, freshVariants) || !this.sameVariantIdentities(freshVariants) || !freshVariants.some(v => v.id === this.current?.id) || !freshVariants.some(v => v.id === this.metre.id)) {
          this.pending = false;
          this.failed = true;
          this.render(false);
          return;
        }
        if (freshVariants) {
          this.variants = freshVariants;
          this.metre = this.variants.find(v => v.id === this.metre.id);
          this.rolls = this.rolls.map(v => this.variants.find(f => f.id === v.id) || v);
          this.current = this.variants.find(v => v.id === this.current.id);
          this.refreshOptions();
        }
        this.pending = false;
        this.failed = false;
        this.quantity.value = this.desiredQuantity;
        this.render(true);
      });
      this.quantityUnsubscribe = subscribe(PUB_SUB_EVENTS.quantityUpdate,() => {
        if (!this.pending) this.render(false);
      });
      this.onPopState = () => {
        if (this.isQuickAdd) return;
        const id = Number(new URL(window.location.href).searchParams.get('variant'));
        const v = this.variants.find(v => v.id === id) || this.metre;
        if (v.id !== this.current?.id) this.select(v.id);
      };
      window.addEventListener('popstate',this.onPopState);
      this.onPageShow = () => this.render(false);
      window.addEventListener('pageshow',this.onPageShow);
      this.observer = new IntersectionObserver(entries => {
        this.root.classList.toggle('fabric-in-view',entries[0].isIntersecting);
      });
      this.observer.observe(this.root);
      this.current = this.variants.find(v => v.id === Number(this.form.querySelector('[name="id"]').value)) || this.metre;
      this.hidden = false;
      this.root.classList.add('fabric-enhanced');
      this.render(true);
    }
    sameVariantIdentities(variants) {
      return variants.length === this.variants.length && variants.every(v => this.variants.some(previous => previous.id === v.id && previous.title === v.title));
    }
    supportsVariants(component, variants) {
      if (!component || Number(component.dataset.optionCount) !== 1 || !Array.isArray(variants) || variants.length !== Number(component.dataset.variantCount) || variants.length === 0 || variants.length > 250) return false;
      if (!variants.every(v => v && Number.isSafeInteger(v.id) && v.id > 0 && typeof v.title === 'string' && Number.isFinite(v.price) && v.price >= 0 && (!v.available || v.price > 0))) return false;
      if (new Set(variants.map(v => v.id)).size !== variants.length) return false;
      if (component.dataset.metreDefault === 'true' && variants.length === 1 && variants[0].title === 'Default Title') return true;
      const metres = variants.filter(v => /^per met(?:er|re)?$/i.test(v.title.trim()));
      const rolls = variants.filter(v => {
        const match = v.title.trim().match(/^roll of\s+(\d+(?:\.\d+)?)\s*met$/i);
        return match && Number.isFinite(Number(match[1])) && Number(match[1]) > 0;
      });
      return metres.length === 1 && rolls.length === variants.length - 1;
    }
    refreshOptions() {
      this.rollSelect.replaceChildren(...this.rolls.map(v => {
        const option = document.createElement('option');
        option.value = v.id;
        option.textContent = `${this.length(v).toFixed(2)} m · ${this.money(v.price)}${v.available ? '' : ' · ' + this.labels.soldOut}`;
        option.disabled = !v.available;
        return option;
      }));
      this.formatInputs.find(input => input.value === 'metre').disabled = !this.metre.available;
      this.formatInputs.find(input => input.value === 'roll').disabled = !this.rolls.some(v => v.available);
    }
    select(id) {
      const next = this.variants.find(v => v.id === id);
      if (!next || (next.id === this.current?.id && !this.failed)) return;
      const radio = this.native && [...this.native.querySelectorAll('input[type="radio"]')].find(input => input.value === next.title);
      if (!radio && !this.defaultMetre) return;
      if (this.current && this.validQuantity()) this.quantities[this.current.id === this.metre.id ? 'metre' : 'roll'] = Number(this.quantity.value);
      this.current = next;
      const mode = next.id === this.metre.id ? 'metre' : 'roll';
      if (mode === 'roll') this.lastRoll = next.id;
      this.desiredQuantity = this.quantities[mode] || next.min || 1;
      this.pending = true;
      this.failed = false;
      this.quantity.value = this.desiredQuantity;
      this.render(true);
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change',{bubbles:true}));
      } else {
        this.loadSelection();
      }
    }
    async loadSelection() {
      this.requestController?.abort();
      const controller = new AbortController();
      this.requestController = controller;
      const generation = this.requestGeneration = (this.requestGeneration || 0) + 1;
      const id = this.current.id;
      this.pending = true;
      this.failed = false;
      this.desiredQuantity = this.desiredQuantity || Number(this.quantity.value) || 1;
      this.render(false);
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const url = new URL(this.native?.dataset.url || this.root.dataset.url, window.location.origin);
        url.searchParams.set('variant', id);
        url.searchParams.set('section_id', this.sourceSection);
        const view = new URL(window.location.href).searchParams.get('view');
        if (view) url.searchParams.set('view', view);
        const response = await fetch(url, {signal: controller.signal});
        if (response.ok === false) throw new Error('Selection unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (generation !== this.requestGeneration || id !== this.current.id || !this.isConnected) return;
        const freshComponent = html.querySelector('fabric-buying');
        const freshVariants = JSON.parse(freshComponent?.querySelector('[data-fabric-variants]')?.textContent || 'null');
        if (!this.supportsVariants(freshComponent, freshVariants) || !this.sameVariantIdentities(freshVariants) || !freshVariants.some(v => v.id === id) || !freshVariants.some(v => v.id === this.metre.id)) throw new Error('Selection incomplete');
        for (const prefix of ['price', 'Sku', 'Inventory']) {
          const destination = this.root.querySelector(`[id="${prefix}-${this.dataset.section}"]`);
          const source = html.getElementById(`${prefix}-${this.sourceSection}`);
          if (destination && source) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('visibility-hidden', source.classList.contains('visibility-hidden'));
          }
        }
        const add = html.getElementById(`ProductSubmitButton-${this.sourceSection}`);
        if (this.native) {
          this.native.toggleAddButton(add ? add.hasAttribute('disabled') : true, window.variantStrings.soldOut);
        } else {
          this.submit.disabled = add ? add.hasAttribute('disabled') : true;
          const text = this.submit.querySelector('span');
          if (text) text.textContent = this.submit.disabled ? window.variantStrings.soldOut : window.variantStrings.addToCart;
        }
        publish(PUB_SUB_EVENTS.variantChange, {data: {sectionId: this.dataset.section, source: this, html, variant: {...(this.native?.currentVariant || this.current), id}}});
      } catch (error) {
        if (generation !== this.requestGeneration || !this.isConnected) return;
        this.pending = false;
        this.failed = true;
        this.render(false);
      } finally {
        clearTimeout(timer);
      }
    }
    bounds() {
      const v = this.current;
      const inCart = this.quantity.dataset.cartQuantity == null ? v.cartQuantity || 0 : Number(this.quantity.dataset.cartQuantity) || 0;
      const candidates = [v.max == null ? Infinity : v.max - inCart];
      if (v.managed && v.policy === 'deny') candidates.push(v.inventory - inCart);
      return {min:Number(this.quantity.dataset.min) || v.min || 1,step:v.step || 1,max:Math.max(0,Math.min(...candidates))};
    }
    validQuantity() {
      if (!this.current) return false;
      const q = Number(this.quantity.value), {min,step,max} = this.bounds();
      return Number.isFinite(q) && Number.isInteger(q) && q >= min && q <= max && (q - min) % step === 0;
    }
    quantityChanged() {
      if (!this.current) return;
      if (this.validQuantity()) {
        this.quantities[this.current.id === this.metre.id ? 'metre' : 'roll'] = Number(this.quantity.value);
        if (this.pending) this.desiredQuantity = Number(this.quantity.value);
      }
      this.render(false);
    }
    render(clamp) {
      if (!this.current) return;
      const v = this.current, roll = v.id !== this.metre.id, {min,step,max} = this.bounds();
      this.quantity.min = min;
      this.quantity.step = step;
      if (Number.isFinite(max)) this.quantity.max = max; else this.quantity.removeAttribute('max');
      if (clamp) {
        const requested = Number(this.quantity.value) || min;
        this.quantity.value = Math.max(min,Math.min(requested,Number.isFinite(max) && max >= min ? max : requested));
      }
      const q = Number(this.quantity.value), valid = this.validQuantity();
      const quantityMessage = v.available ? this.labels.quantityError : this.labels.soldOut;
      this.stock.hidden = this.pending || this.failed || !Number.isFinite(max);
      this.stock.textContent = !v.available ? this.labels.soldOut : `${this.labels.stockLimit} ${max} ${roll ? max === 1 ? this.labels.roll : this.labels.rolls : max === 1 ? this.labels.metre : this.labels.metres}`;
      for (const input of this.formatInputs) input.checked = input.value === (roll ? 'roll' : 'metre');
      this.rollField.hidden = !roll;
      if (roll) this.rollSelect.value = v.id;
      const label = this.root.querySelector('.quantity__label');
      if (label) {
        const text = [...label.childNodes].find(node => node.nodeType === Node.TEXT_NODE);
        if (text) text.textContent = (roll ? this.labels.quantityRolls : this.labels.quantityMetres) + ' ';
      }
      const price = document.createElement('strong');
      price.textContent = this.money(roll ? Math.round(v.price / this.length(v)) : v.price);
      const unit = document.createElement('small');unit.textContent = ' ' + this.labels.perMetre;
      const detail = document.createElement('span');detail.className = 'fabric-price-detail';
      detail.textContent = roll ? `${this.length(v).toFixed(2)} m ${this.labels.roll} · ${this.money(v.price)} ${this.labels.perRoll}` : '';
      if (v.compare > v.price) {const strike = document.createElement('s');strike.textContent = `${this.money(v.compare)} ${roll ? this.labels.perRoll : this.labels.perMetre}`;if (roll) detail.append(document.createTextNode(' · '));detail.append(strike);}
      this.unitPrice.replaceChildren(price,unit,detail);
      this.detail.textContent = valid ? roll ? `${(this.length(v) * q).toFixed(2)} ${this.labels.fabricTotal}` : `${q} m × ${this.money(v.price)}` : quantityMessage;
      const totalAmount = document.createElement('strong');totalAmount.textContent = valid ? this.money(v.price * q) : '—';
      const summary = document.createElement('small');summary.textContent = valid ? `${q} ${roll ? q === 1 ? this.labels.roll : this.labels.rolls : q === 1 ? this.labels.metre : this.labels.metres}` : quantityMessage;
      this.total.replaceChildren(document.createTextNode(this.labels.total),totalAmount,summary);
      this.submit.disabled = this.pending || this.failed || !v.available || !valid;
      this.retry.hidden = !this.failed;
      this.status.textContent = this.pending ? this.labels.updating : this.failed ? this.labels.selectionError : !v.available ? this.labels.soldOut : !valid ? this.labels.quantityError : '';
      this.quantity.setCustomValidity(valid ? '' : this.labels.quantityError + (Number.isFinite(max) ? ` (${this.labels.stockLimit} ${max})` : ''));
    }
    disconnectedCallback() {
      this.requestController?.abort();
      if (this.native && this.originalRenderProductInfo) this.native.renderProductInfo = this.originalRenderProductInfo;
      this.variantUnsubscribe?.();this.quantityUnsubscribe?.();this.observer?.disconnect();
      window.removeEventListener('popstate',this.onPopState);window.removeEventListener('pageshow',this.onPageShow);
      if (this.native && this.originalUpdateUrl) this.native.updateURL = this.originalUpdateUrl;
      if (this.native && this.originalUpdateVariantInput) this.native.updateVariantInput = this.originalUpdateVariantInput;
      if (this.native && this.originalToggleAddButton) this.native.toggleAddButton = this.originalToggleAddButton;
    }
  });
}


